import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchAllSubmissions, updateSubmissionStatus } from '../../redux/Actions/consultantActions';
import { 
    Container, Typography, Box, Paper, Table, TableBody, TableCell, 
    TableContainer, TableHead, TableRow, Button, Chip, IconButton,
    Dialog, DialogTitle, DialogContent, DialogActions, Grid, TextField, Stack, Alert
} from '@mui/material';
import { Visibility, CheckCircle, Cancel, Send, Warning } from '@mui/icons-material';
import ConsultantNavbar from '../../Components/Dashboard/ConsultantDashboard/ConsultantNavbar';
import api from 'lib/api/api';

const ConsultantSubmissionsPage = () => {
    const dispatch = useDispatch();
    const { submissions, loading } = useSelector(state => state.consultant);
    const [selectedSubmission, setSelectedSubmission] = useState(null);
    const [openModal, setOpenModal] = useState(false);
    const [message, setMessage] = useState('');
    const [sendingMessage, setSendingMessage] = useState(false);

    useEffect(() => {
        dispatch(fetchAllSubmissions());
    }, [dispatch]);

    const handleViewDetails = (submission) => {
        setSelectedSubmission(submission);
        setOpenModal(true);
        setMessage(''); // Reset message box
    };

    const handleStatusUpdate = (id, status) => {
        dispatch(updateSubmissionStatus(id, status));
        setOpenModal(false);
    };

    const handleSendMessage = async () => {
        if (!message.trim()) return;
        setSendingMessage(true);
        try {
            await api.post(`/consultant/submissions/${selectedSubmission.id}/message`, {
                message: message,
                studentId: selectedSubmission.studentId
            });
            alert('Message sent successfully to student notification bell!');
            setMessage('');
        } catch (error) {
            console.error('Error sending message:', error);
            alert('Failed to send message.');
        } finally {
            setSendingMessage(false);
        }
    };

    const getMissingFields = (sub) => {
        if (!sub) return [];
        const universityRequired = [
            { key: 'firstName', label: 'First Name' },
            { key: 'lastName', label: 'Last Name' },
            { key: 'email', label: 'Email' },
            { key: 'phone', label: 'Phone' },
            { key: 'dob', label: 'Date of Birth' },
            { key: 'nationality', label: 'Nationality' },
            { key: 'passportNumber', label: 'Passport Number' },
            { key: 'city', label: 'City' },
            { key: 'previousQualification', label: 'Previous Qualification' },
            { key: 'institutionName', label: 'Institution Name' },
            { key: 'graduationYear', label: 'Graduation Year' },
            { key: 'gpa', label: 'GPA' },
            { key: 'desiredProgram', label: 'Desired Program' },
            { key: 'programLevel', label: 'Program Level' },
            { key: 'certificationsFile', label: 'Certifications Document' },
            { key: 'highSchoolFile', label: 'High School Document' },
            { key: 'passportFile', label: 'Passport Document' },
            { key: 'transcriptFile', label: 'Transcript Document' },
            { key: 'motivationFile', label: 'Motivation Letter' },
        ];
        const ausbildungRequired = [
            { key: 'firstName', label: 'First Name' },
            { key: 'lastName', label: 'Last Name' },
            { key: 'email', label: 'Email' },
            { key: 'desiredProgram', label: 'Desired Ausbildung' },
            { key: 'germanLevel', label: 'German Language Level' },
            { key: 'certificationsFile', label: 'Language Certificate' },
            { key: 'highSchoolFile', label: 'School Certificate' },
            { key: 'motivationFile', label: 'Motivation Letter' },
            { key: 'audioMessageFile', label: 'Audio Message' },
        ];
        const required = sub.category === 'ausbildung' ? ausbildungRequired : universityRequired;
        return required.filter(f => !sub[f.key]).map(f => f.label);
    };

    const getDocumentList = (sub) => (
        sub?.category === 'ausbildung'
            ? [
                { key: 'certificationsFile', label: 'Language Certificate' },
                { key: 'highSchoolFile', label: 'School Certificate' },
                { key: 'motivationFile', label: 'Motivation Letter' },
                { key: 'audioMessageFile', label: 'Audio Message' }
            ]
            : [
                { key: 'certificationsFile', label: 'Certifications' },
                { key: 'highSchoolFile', label: 'High School' },
                { key: 'passportFile', label: 'Passport' },
                { key: 'transcriptFile', label: 'Transcript' },
                { key: 'motivationFile', label: 'Motivation' }
            ]
    );

    const getStatusChip = (status) => {
        const name = (typeof status === 'string' ? status : status?.name)?.toLowerCase() || 'pending';
        switch (name) {
            case 'approved': return <Chip label="Approved" color="success" size="small" sx={{ fontWeight: 700 }} />;
            case 'rejected': return <Chip label="Rejected" color="error" size="small" sx={{ fontWeight: 700 }} />;
            default: return <Chip label="Pending" color="warning" size="small" sx={{ fontWeight: 700 }} />;
        }
    };

    return (
        <div style={{ backgroundColor: '#f5f7f8', minHeight: '100vh' }}>
            <ConsultantNavbar />
            <Container sx={{ mt: 4, pb: 6 }}>
                <Typography variant="h4" fontWeight={900} gutterBottom sx={{ color: '#2D3E50', letterSpacing: -1 }}>
                    Student Submissions
                </Typography>
                
                <TableContainer component={Paper} sx={{ borderRadius: 4, overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
                    <Table>
                        <TableHead sx={{ bgcolor: '#f8f9fa' }}>
                            <TableRow>
                                <TableCell><strong>Student</strong></TableCell>
                                <TableCell><strong>Program</strong></TableCell>
                                <TableCell><strong>Type</strong></TableCell>
                                <TableCell><strong>Status</strong></TableCell>
                                <TableCell><strong>Submitted At</strong></TableCell>
                                <TableCell align="center"><strong>Actions</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>Loading submissions...</TableCell>
                                </TableRow>
                            ) : Array.isArray(submissions) && submissions.length > 0 ? (
                                submissions.map((sub) => (
                                    <TableRow key={sub.id} hover>
                                        <TableCell sx={{ fontWeight: 600 }}>{`${sub.firstName} ${sub.lastName}`}</TableCell>
                                        <TableCell>{sub.desiredProgram}</TableCell>
                                        <TableCell sx={{ textTransform: 'capitalize' }}>{sub.category}</TableCell>
                                        <TableCell>{getStatusChip(sub.status)}</TableCell>
                                        <TableCell>{new Date(sub.createdAt).toLocaleDateString()}</TableCell>
                                        <TableCell align="center">
                                            <IconButton color="primary" onClick={() => handleViewDetails(sub)} sx={{ bgcolor: 'rgba(25, 118, 210, 0.08)' }}>
                                                <Visibility />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>No submissions found.</TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Container>

            {/* Review Modal */}
            <Dialog open={openModal} onClose={() => setOpenModal(false)} maxWidth="md" fullWidth>
                <DialogTitle sx={{ fontWeight: 800, px: 3, pt: 3, pb: 1, fontSize: '1.5rem', color: '#2D3E50' }}>
                    Application Details
                </DialogTitle>
                <DialogContent sx={{ px: 3 }}>
                    {selectedSubmission && (
                        <Grid container spacing={3} sx={{ mt: 0.5 }}>
                            {/* Missing Fields Alert */}
                            {getMissingFields(selectedSubmission).length > 0 && (
                                <Grid item xs={12}>
                                    <Alert 
                                        severity="warning" 
                                        icon={<Warning fontSize="inherit" />}
                                        sx={{ borderRadius: 3, border: '1px solid #ff9800' }}
                                    >
                                        <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                                            Missing Documents/Information:
                                        </Typography>
                                        <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 1 }}>
                                            {getMissingFields(selectedSubmission).map((f, i) => (
                                                <Chip key={i} label={f} size="small" color="warning" variant="outlined" sx={{ bgcolor: 'white' }} />
                                            ))}
                                        </Stack>
                                    </Alert>
                                </Grid>
                            )}

                            {/* Section: Personal Info */}
                            <Grid item xs={12}>
                                <Typography variant="h6" sx={{ fontWeight: 800, borderBottom: '2px solid #eee', pb: 1, color: '#2D3E50' }}>Personal Information</Typography>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Typography variant="caption" color="text.secondary">Full Name</Typography>
                                <Typography variant="body1" fontWeight={600}>{selectedSubmission.firstName} {selectedSubmission.lastName}</Typography>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Typography variant="caption" color="text.secondary">Email</Typography>
                                <Typography variant="body1" fontWeight={600}>{selectedSubmission.email}</Typography>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Typography variant="caption" color="text.secondary">Phone</Typography>
                                <Typography variant="body1" fontWeight={600}>{selectedSubmission.phone}</Typography>
                            </Grid>

                            {/* Section: Application Info */}
                            <Grid item xs={12}>
                                <Typography variant="h6" sx={{ fontWeight: 800, borderBottom: '2px solid #eee', pb: 1, mt: 2, color: '#2D3E50' }}>
                                    {selectedSubmission.category === 'ausbildung' ? 'Ausbildung Information' : 'Academic Information'}
                                </Typography>
                            </Grid>
                            {selectedSubmission.category !== 'ausbildung' && (
                                <Grid item xs={12} md={4}>
                                    <Typography variant="caption" color="text.secondary">GPA</Typography>
                                    <Typography variant="body1" fontWeight={600}>{selectedSubmission.gpa || 'N/A'}</Typography>
                                </Grid>
                            )}
                            <Grid item xs={12} md={4}>
                                <Typography variant="caption" color="text.secondary">
                                    {selectedSubmission.category === 'ausbildung' ? 'Desired Ausbildung' : 'Program'}
                                </Typography>
                                <Typography variant="body1" fontWeight={600}>{selectedSubmission.desiredProgram}</Typography>
                            </Grid>
                            {selectedSubmission.category === 'ausbildung' ? (
                                <Grid item xs={12} md={4}>
                                    <Typography variant="caption" color="text.secondary">German Language Level</Typography>
                                    <Typography variant="body1" fontWeight={600}>{selectedSubmission.germanLevel || 'N/A'}</Typography>
                                </Grid>
                            ) : (
                                <Grid item xs={12} md={4}>
                                    <Typography variant="caption" color="text.secondary">Level</Typography>
                                    <Typography variant="body1" fontWeight={600}>{selectedSubmission.programLevel}</Typography>
                                </Grid>
                            )}
                            {selectedSubmission.category === 'ausbildung' && (
                                <Grid item xs={12} md={4}>
                                    <Typography variant="caption" color="text.secondary">Other Languages</Typography>
                                    <Typography variant="body1" fontWeight={600}>{selectedSubmission.otherLanguages || 'N/A'}</Typography>
                                </Grid>
                            )}

                            {/* Section: Documents */}
                            <Grid item xs={12}>
                                <Typography variant="h6" sx={{ fontWeight: 800, borderBottom: '2px solid #eee', pb: 1, mt: 2, color: '#2D3E50' }}>Uploaded Documents</Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                                    {getDocumentList(selectedSubmission).map((doc) => (
                                        selectedSubmission[doc.key] && (
                                            <Button 
                                                key={doc.key}
                                                variant="outlined" 
                                                size="medium" 
                                                href={`${process.env.REACT_APP_API_URL}/api/student-admissions/download/${selectedSubmission[doc.key]}`} 
                                                target="_blank"
                                                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                                            >
                                                View {doc.label}
                                            </Button>
                                        )
                                    ))}
                                </Box>
                            </Grid>

                            {/* Section: Message Area */}
                            <Grid item xs={12}>
                                <Box sx={{ mt: 3, p: 3, bgcolor: '#f1f5f9', borderRadius: 4, border: '1px solid #cbd5e1' }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1, color: '#2D3E50', display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Send fontSize="small" /> Send Real-time Feedback
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={3}
                                        variant="outlined"
                                        placeholder="Type a message to the student (e.g., Please re-upload a clearer passport photo)..."
                                        value={message}
                                        onChange={(e) => setMessage(e.target.value)}
                                        sx={{ bgcolor: 'white', '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
                                    />
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        sx={{ mt: 2, py: 1.5, fontWeight: 800, borderRadius: 3, textTransform: 'none', boxShadow: 'none' }}
                                        onClick={handleSendMessage}
                                        disabled={sendingMessage || !message.trim()}
                                    >
                                        {sendingMessage ? 'Sending...' : 'Send Message to Student Bell'}
                                    </Button>
                                </Box>
                            </Grid>
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 3, gap: 1 }}>
                    <Button onClick={() => setOpenModal(false)} variant="outlined" sx={{ borderRadius: 2, px: 3 }}>Close</Button>
                    <Box sx={{ flexGrow: 1 }} />
                    <Button 
                        variant="contained" 
                        color="error" 
                        startIcon={<Cancel />}
                        onClick={() => handleStatusUpdate(selectedSubmission.id, 'rejected')}
                        sx={{ borderRadius: 2, px: 3, fontWeight: 700 }}
                    >
                        Reject
                    </Button>
                    <Button 
                        variant="contained" 
                        color="success" 
                        startIcon={<CheckCircle />}
                        onClick={() => handleStatusUpdate(selectedSubmission.id, 'approved')}
                        sx={{ borderRadius: 2, px: 3, fontWeight: 700 }}
                    >
                        Approve
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default ConsultantSubmissionsPage;
